import React, { useState, useCallback } from 'react';

interface Preset {
  label: string;
  value: number;
}

interface CurrencyInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  presets?: Preset[];
  disabled?: boolean;
  helperText?: string;
  placeholder?: string;
}

const DEFAULT_PRESETS: Preset[] = [
  { label: '$1M', value: 1000000 },
  { label: '$5M', value: 5000000 },
  { label: '$10M', value: 10000000 },
  { label: '$30M', value: 30000000 },
  { label: '$50M+', value: 50000000 },
  { label: 'TBD', value: 0 },
];

function formatDisplayValue(num: number): string {
  if (num === 0) return '';
  return num.toLocaleString('en-US');
}

function parseInputValue(str: string): number {
  const cleaned = str.replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * CurrencyInput — Formatted currency input with quick-select preset chips.
 * Used for Annualized Entitlement in the StructuredCaptureCard.
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  label,
  value,
  onChange,
  presets,
  disabled = false,
  helperText,
  placeholder = 'Or type a custom amount...'
}) => {
  const activePresets = presets && presets.length > 0 ? presets : DEFAULT_PRESETS;
  const [inputValue, setInputValue] = useState(value > 0 ? formatDisplayValue(value) : '');
  const [isFocused, setIsFocused] = useState(false);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = parseInputValue(raw);
    onChange(parsed);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Format on blur
    if (value > 0) {
      setInputValue(formatDisplayValue(value));
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // Show raw number on focus for easier editing
    if (value > 0) {
      setInputValue(value.toString());
    }
  }, [value]);

  const handlePresetClick = useCallback((presetValue: number) => {
    onChange(presetValue);
    setInputValue(presetValue > 0 ? formatDisplayValue(presetValue) : '');
  }, [onChange]);

  return (
    <div className="currency-input">
      <div className="currency-input-label">{label}</div>
      {helperText && <div className="currency-input-helper">{helperText}</div>}
      <div className="currency-input-presets">
        {activePresets.map(preset => (
          <button
            key={preset.label}
            type="button"
            className={`currency-preset${value === preset.value ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
            onClick={() => !disabled && handlePresetClick(preset.value)}
            disabled={disabled}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className={`currency-input-field${isFocused ? ' focused' : ''}${value > 0 ? ' has-value' : ''}`}>
        <span className="currency-input-prefix">$</span>
        <input
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="currency-input-text"
        />
      </div>
    </div>
  );
};

export default CurrencyInput;